import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentUser } from '@economic/react-app';

import useQuery from '~/app/shared/hooks/useQuery';
import { Journal } from '~/types/internal-api';
import { CompanyResponse } from '~/payment-cards-feature';
import { BankAccount, SelectableAccount } from '~/types';
import { getCheckCustomer } from '../api';
import { getValidFeeAccountsForSmartPay, getValidJournalsForSmartPay } from '../../../../payments/utils';
import {
    DEFAULT_FEE_ACCOUNT_NUMBER,
    NFCustomerDataType,
    SMARTPAY_PLAN_OPTIONS,
    SmartPayCustomerType,
    SmartPayOnboardingFormData,
} from '../../../../types';

export interface SignupDialogOnboardingContextType {
    isLoadingOnboardingData: boolean;
    NFCustomerData: NFCustomerDataType | null;
    formData: Partial<SmartPayOnboardingFormData> | null;
    setFormData: React.Dispatch<React.SetStateAction<Partial<SmartPayOnboardingFormData> | null>>;
    bankAccounts: BankAccount[];
    feeAccounts: SelectableAccount[];
    journals: Journal[];
    companyData: CompanyResponse;
    refetchCompanyData: () => Promise<void>;
    refetchBankAccounts: () => Promise<void>;
    onboardError: string | null;
    setOnboardError: React.Dispatch<React.SetStateAction<string | null>>;
    setFormDefaults: () => void;
}
// context used to store onboarding form data used by the useSignupDialogForm hook
export const SignupDialogOnboardingContext = createContext<SignupDialogOnboardingContextType | null>(null);

const SignupDialogOnboardingContextProvider = ({ children }) => {
    const user = useCurrentUser();

    const {
        data: companyData,
        refetch: refetchCompanyData,
        loading: loadingCompanyData,
    } = useQuery<CompanyResponse>('/company/getCurrentCompany');
    const {
        data: bankAccounts,
        refetch: refetchBankAccounts,
        loading: loadingBankAccounts,
    } = useQuery<BankAccount[]>('/paymentAccounts/accounts');
    const { data: allJournals, loading: loadingJournals } = useQuery<Journal[]>('/journals');
    const { data: allFeeAccounts, loading: loadingFeeAccounts } = useQuery<SelectableAccount[]>('/accounts');

    const [NFCustomerData, setNFCustomerData] = useState<NFCustomerDataType | null>(null);
    const [formData, setFormData] = useState<Partial<SmartPayOnboardingFormData> | null>(null);
    const [onboardError, setOnboardError] = useState<string | null>(null);

    const validJournals = useMemo(() => getValidJournalsForSmartPay(allJournals), [allJournals]);
    const validFeeAccounts = useMemo(() => getValidFeeAccountsForSmartPay(allFeeAccounts), [allFeeAccounts]);
    const isLoadingOnboardingData = loadingCompanyData || loadingBankAccounts || loadingJournals || loadingFeeAccounts;
    const hasFormDefaultsBeenSet = useRef(false);

    const updateFormState = (properties: object) => {
        setFormData(prevFormData => ({
            ...prevFormData,
            ...properties,
        }));
    };

    useEffect(() => {
        if (isLoadingOnboardingData) return;

        if (!hasFormDefaultsBeenSet.current) setFormDefaults();

        // check customer when company data is available or when company data updates
        if (companyData) {
            updateFormState({ cvr: companyData?.model.cvr, company: companyData?.model.name });
            checkCustomer();
        }
        // run whenever loading finishes, CVR changes, or bank accounts mutate
    }, [isLoadingOnboardingData, companyData?.model?.cvr]);

    useEffect(() => {
        // ExistingCustomerWithIntegration should not have a default bank account or fee journal number
        if (NFCustomerData?.customerType !== SmartPayCustomerType.ExistingCustomerWithIntegration) {
            updateFormState({
                bankAccount: bankAccounts?.[0]?.number,
                feeJournalNumber:
                    validFeeAccounts.length && validFeeAccounts.some(a => a.number === DEFAULT_FEE_ACCOUNT_NUMBER)
                        ? DEFAULT_FEE_ACCOUNT_NUMBER
                        : undefined,
            });
        }
    }, [NFCustomerData, bankAccounts, validFeeAccounts]);

    const setFormDefaults = () => {
        updateFormState({
            email: user?.email,
            firstName: user?.firstName,
            lastName: user?.lastName,
            cvr: companyData?.model.cvr,
            company: companyData?.model.name,
            nfCustomerNumber: NFCustomerData?.novemberFirstCustomerNumberList?.[0],
            bankAccount: null,
            journalNumber: 0, // create new Smart Pay journal by default
            feeJournalNumber: null,
            subscriptionType: SMARTPAY_PLAN_OPTIONS[0].id,
        });

        hasFormDefaultsBeenSet.current = true;
    };

    const checkCustomer = async () => {
        if (!companyData) {
            setOnboardError('');
            return;
        }

        if (companyData.model.cvr === undefined) {
            return;
        }

        try {
            const { data: NFData } = await getCheckCustomer(companyData.model.cvr);

            if (!NFData) return;
            const customerNumbers = NFData.customerNumbers ?? [];

            setNFCustomerData({
                customerType: NFData.customerType ? NFData.customerType : SmartPayCustomerType.NewCustomer,
                cvrAlreadyOnboarded: !!NFData.cvrAlreadyOnboarded,
                anotherOnboardingAlreadyInProgress: !!NFData.onboardingAlreadyInProgress,
                allCustomersOnboarded: !!NFData.allCustomersOnboarded,
                novemberFirstCustomerNumberList: customerNumbers,
            });

            // preselect first customer number
            if (!customerNumbers.length) return;
            updateFormState({ nfCustomerNumber: customerNumbers[0] });
        } catch (_) {
            setOnboardError('');
        }
    };

    const contextData = {
        isLoadingOnboardingData,
        formData,
        setFormData,
        bankAccounts,
        feeAccounts: validFeeAccounts,
        journals: validJournals,
        companyData,
        NFCustomerData,
        refetchCompanyData,
        refetchBankAccounts,
        onboardError,
        setOnboardError,
        setFormDefaults,
    };

    return <SignupDialogOnboardingContext.Provider value={contextData}>{children}</SignupDialogOnboardingContext.Provider>;
};

export default SignupDialogOnboardingContextProvider;

export function useSignupDialogOnboardingContext(): SignupDialogOnboardingContextType {
    const context = useContext(SignupDialogOnboardingContext);

    if (!context) {
        throw new Error('useSignupDialogOnboardingContext must be nested inside a SignupDialogOnboardingContextProvider');
    }

    return context;
}
